import { useState } from 'react';
import { config } from '@uhg-abyss/web/tools/config';
import { jsonKey } from '@uhg-abyss/web/tools/jsonKey';
import { storage } from '@uhg-abyss/web/tools/storage';
import { event } from '@uhg-abyss/web/tools/event';
import { stopwatch } from '@uhg-abyss/web/tools/stopwatch';
import { retry } from '../retry';

const CACHE = {};
const MAX_TRIES = 2;
const RETRY_INTERVAL = 1000;
const CACHE_MAX_AGE = 15 * 60 * 1000; // 15 minutes
const DEFAULT_ERROR = `The system is unable to respond at the moment. \
  Please try refreshing the page (click on REFRESH at the top of the page \
  or press F5) or try again at a later time.`;

const memoryStore = {
  getItem: (key) => {
    return CACHE[key];
  },
  setItem: (key, value) => {
    CACHE[key] = value;
  },
  removeItem: (key) => {
    delete CACHE[key];
  },
};

const memoryCache = {
  get: (key) => storage.getItem(memoryStore, key),
  set: (key, value) => storage.setItem(memoryStore, key, value, CACHE_MAX_AGE),
  del: (key) => memoryStore.removeItem(key),
};

const initialState = {
  loading: false,
  data: null,
  error: null,
};

export const useQueryHook = (query, queryOptions) => {
  const options = queryOptions || {};
  const [queryResult, setQueryResult] = useState(initialState);

  const queryAction = (params) => {
    if (options.onCalled) {
      options.onCalled();
    }
    const queryName = query.definitions.find((def) => {
      return def.kind === 'OperationDefinition';
    }).name.value;
    const queryBody = query.loc.source.body;
    const queryVariables = params.variables;
    const token = `${query.documentId}:${jsonKey(queryVariables)}`;
    const cachedRequest = memoryCache.get(token);
    const eventData = event.getData();

    setQueryResult({
      ...initialState,
      loading: true,
    });

    if (cachedRequest) {
      return cachedRequest.then(
        (response) => {
          if (options.onCompleted) {
            options.onCompleted(response);
          }

          setQueryResult({
            ...initialState,
            data: response.body.data,
          });

          return response;
        },
        (error) => {
          if (options.onError) {
            options.onError(error);
          }

          setQueryResult({
            ...initialState,
            error,
          });

          throw error;
        }
      );
    }

    const timer = stopwatch.start();

    const eapm = window.elasticApm;
    let transaction;

    // callback created for retry function
    const updateElasticApm = () => {
      if (eapm) {
        transaction = window.elasticApm.startTransaction(
          queryName,
          'http-request',
          {
            managed: false,
          }
        );
      }
    };

    const makeRequest = () =>
      fetch(`${options.url}?q=${queryName}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          uuid: eventData.uuid || options.uuid,
          'x-xss-protection': '1',
          'content-security-policy': `default-src 'none'; img-src 'self'; script-src 'unsafe-inline'; style-src 'self'`,
          'referrer-policy': 'origin',
          'x-content-type-options': 'nosniff',
        },
        body: JSON.stringify({
          operationName: queryName,
          query: queryBody,
          variables: queryVariables,
        }),
      })
        .then((response) => {
          return response.json().then((json) => {
            return {
              status: response.status,
              duration: timer.stop(),
              body: json,
            };
          });
        })
        .then((response) => {
          const { body } = response;

          if (body.errors) {
            const serverError = body.errors[0];
            const { extensions } = serverError;
            const { exception } = extensions;

            serverError.type = extensions.code;
            serverError.status = response.status;
            serverError.duration = response.duration;

            if (extensions.response) {
              serverError.status = extensions.response.status;
            }

            if (exception?.code) {
              serverError.code = exception.code;
            }

            const debug = {
              operationName: queryName,
              type: serverError.type,
              variables: jsonKey(queryVariables),
              code: serverError.code,
              path: serverError.path,
              stack: exception?.stacktrace,
            };

            serverError.debug = JSON.stringify(debug);

            throw serverError;
          }
          event('API_TRANSACTION_SUCCESS', {
            ...options.info,
            operationName: queryName,
            status: response.status,
            duration: response.duration,
            transaction,
          });

          if (options.onCompleted) {
            options.onCompleted(response);
          }

          setQueryResult({
            ...initialState,
            data: body.data,
          });

          if (transaction) transaction.end();
          return response;
        })
        .catch((err) => {
          memoryCache.del(token);

          const error = err;
          error.operationName = queryName;
          if (!error.type) {
            error.type = 'NETWORK_ERROR';
            error.message = DEFAULT_ERROR;
            error.duration = timer.stop();
          }
          event('API_TRANSACTION_ERROR', {
            ...options.info,
            operationName: error.operationName,
            status: error.status,
            duration: error.duration,
            code: error.code,
            message: error.message,
            debug: error.debug,
            originApp: options.info.resourceId,
            transaction,
          });

          setQueryResult({
            ...initialState,
            error,
          });

          if (transaction) transaction.end();

          throw error;
        });

    const request = retry(
      makeRequest,
      MAX_TRIES,
      RETRY_INTERVAL,
      updateElasticApm,
      options?.onError
    );

    if (options.requestPolicy !== 'no-cache') {
      memoryCache.set(token, request);
    }

    return request;
  };
  return [queryResult, queryAction];
};

export const useQuery = (query, queryOptions) => {
  return useQueryHook(query, {
    ...queryOptions,
    url: queryOptions.url || config('CORE_GRAPHQL_API_URL'),
    info: {
      resourceId: 'core',
    },
  });
};
